import styled from 'styled-components';
import {
    borderColor,
    borderRadius,
    cardBackground,
    mutedForeground,
    primaryColor,
    primaryColorTransparent,
    primaryForeground,
    primaryHover,
} from '../../utils/variables';

export const Root = styled.div<{ $compact?: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${({$compact}) => ($compact ? '100%' : '100%')};
  min-width: 0;

  /* Main open-catalog triggers fill the embed slot; nested + buttons stay auto-sized */
  & > button {
    width: 100%;
  }
`;

export const SearchShell = styled.div<{ $focused?: boolean; $large?: boolean; $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  min-height: ${({$large, $compact}) => {
    if ($compact) return '36px';
    return $large ? '44px' : '36px';
  }};
  padding: ${({$large, $compact}) => {
    if ($compact) return '4px 10px';
    return $large ? '8px 12px' : '4px 10px';
  }};
  border: 1px solid ${({$focused}) => ($focused ? primaryColor : borderColor)};
  border-radius: ${borderRadius};
  background: ${cardBackground};
  box-shadow: ${({$focused}) => ($focused ? `0 0 0 2px ${primaryColorTransparent}` : 'none')};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
`;

export const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--foreground);
  font-size: 13px;
  line-height: 1.4;

  &::placeholder {
    color: ${mutedForeground};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const Dropdown = styled.div<{ $maxHeight?: number; $portal?: boolean }>`
  ${({$portal}) => ($portal ? `
    position: fixed;
    z-index: 1300;
  ` : `
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    z-index: 20;
  `)}
  max-height: ${({$maxHeight}) => ($maxHeight ? `${$maxHeight}px` : '280px')};
  overflow: auto;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  background: ${cardBackground};
  box-shadow: var(--card-shadow-lg);
`;

export const ResultRow = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid var(--table-row-border);
  background: ${({$active}) => ($active ? 'var(--table-row-hover)' : 'transparent')};
  color: var(--foreground);
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    background: var(--nav-item-hover-bg);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.75;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const ResultMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

export const ResultTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ResultMeta = styled.span`
  font-size: 11px;
  color: ${mutedForeground};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ResultPrice = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--foreground);
  padding-top: 1px;
`;

export const EmptyState = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: ${mutedForeground};
  font-size: 13px;
`;

export const Hint = styled.div<{ $compact?: boolean }>`
  display: ${({$compact}) => ($compact ? 'none' : 'block')};
  margin-top: 6px;
  font-size: 12px;
  color: ${mutedForeground};
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  background: ${primaryColorTransparent};
  color: ${primaryColor};
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
`;

export const TriggerButton = styled.button<{ $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: ${({$compact}) => ($compact ? '36px' : '40px')};
  padding: 0 14px;
  border: none;
  border-radius: ${borderRadius};
  background: ${primaryColor};
  color: ${primaryForeground};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  width: auto;
  max-width: 100%;

  &:hover:not(:disabled) {
    background: ${primaryHover};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const PanelOverlay = styled.div<{ $fullscreen?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: ${({$fullscreen}) => ($fullscreen ? cardBackground : 'rgba(15, 23, 42, 0.45)')};
  display: flex;
  justify-content: ${({$fullscreen}) => ($fullscreen ? 'stretch' : 'flex-end')};
`;

export const Panel = styled.div<{ $wide?: boolean }>`
  width: ${({$wide}) => ($wide ? 'min(920px, 100vw)' : 'min(720px, 100vw)')};
  height: 100%;
  background: ${cardBackground};
  box-shadow: var(--card-shadow-lg);
  display: flex;
  flex-direction: column;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${borderColor};
  flex-shrink: 0;
`;

export const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
  overflow: hidden;
`;

export const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid ${borderColor};
  flex-shrink: 0;
`;

export const Split = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  min-height: 0;
  flex: 1;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const CategoryList = styled.div`
  overflow: auto;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  background: var(--muted);
  min-height: 0;
`;

export const CategoryItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid ${borderColor};
  background: ${({$active}) => ($active ? cardBackground : 'transparent')};
  color: var(--foreground);
  cursor: pointer;
  text-align: left;
  font-size: 13px;

  &:hover {
    background: ${cardBackground};
  }
`;

export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  overflow: auto;
  align-content: start;
  padding: 2px;
  min-height: 0;
`;

export const Tile = styled.button<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid ${({$selected}) => ($selected ? primaryColor : borderColor)};
  border-radius: ${borderRadius};
  background: ${cardBackground};
  box-shadow: ${({$selected}) => ($selected ? `0 0 0 1px ${primaryColor}` : 'none')};
  cursor: pointer;
  text-align: left;
  color: var(--foreground);
  min-height: 160px;
  min-width: 0;

  &:hover {
    border-color: ${primaryColor};
  }
`;

export const TileImage = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  background: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: ${mutedForeground};
  font-size: 12px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ModeCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
`;

export const ModeCard = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  border: 1px solid ${({$active}) => ($active ? primaryColor : borderColor)};
  border-radius: ${borderRadius};
  background: ${({$active}) => ($active ? primaryColorTransparent : cardBackground)};
  text-align: left;
  cursor: pointer;
  color: var(--foreground);

  &:hover {
    border-color: ${primaryColor};
  }
`;

export const SettingsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SettingsLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${mutedForeground};
`;

export const PageTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-bottom: 1px solid ${borderColor};
`;

export const PageTab = styled.button<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  background: transparent;
  color: ${({$active}) => ($active ? primaryColor : 'var(--foreground)')};
  font-size: 14px;
  font-weight: ${({$active}) => ($active ? 700 : 500)};
  cursor: pointer;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({$active}) => ($active ? primaryColor : 'transparent')};
  }

  &:hover {
    color: ${primaryColor};
  }
`;

export const PageTabMeta = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${mutedForeground};
  padding: 2px 7px;
  border-radius: 999px;
  background: var(--muted);
`;

export const ModeSetupGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 220px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const ModeChipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ModeChip = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid ${({$active}) => ($active ? primaryColor : borderColor)};
  border-radius: ${borderRadius};
  background: ${({$active}) => ($active ? primaryColorTransparent : cardBackground)};
  color: var(--foreground);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${primaryColor};
  }
`;

export const ModeChipIcon = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  background: ${({$active}) => ($active ? primaryColor : 'var(--muted)')};
  color: ${({$active}) => ($active ? primaryForeground : primaryColor)};
  font-size: 15px;
`;

export const ModeChipTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
`;

export const ModeDetailsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 14px;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  background: ${cardBackground};
`;

export const ModeDetailsTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--foreground);
`;

export const ModeDetailsText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${mutedForeground};
`;

export const VariationOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

export const VariationDialog = styled.div`
  width: min(420px, 100%);
  max-height: min(80vh, 520px);
  overflow: auto;
  border-radius: ${borderRadius};
  background: ${cardBackground};
  box-shadow: var(--card-shadow-lg);
  padding: 16px;
`;

export const VariationBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const VariationOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const VariationChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  border: 1px solid ${({$active}) => ($active ? primaryColor : borderColor)};
  background: ${({$active}) => ($active ? primaryColorTransparent : cardBackground)};
  color: var(--foreground);
  border-radius: ${borderRadius};
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  text-align: left;
  max-width: 100%;
`;

export const VariationChipName = styled.span`
  font-weight: 600;
  line-height: 1.3;
`;

export const VariationChipPrice = styled.span`
  font-size: 12px;
  color: ${mutedForeground};
  line-height: 1.3;
`;

export const PreviewFrame = styled.div`
  margin-top: 12px;
  padding: 14px;
  border: 1px dashed ${borderColor};
  border-radius: ${borderRadius};
  background: var(--muted);
  min-height: 72px;
`;

export const ScanPulse = styled.div`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${primaryColor};
  box-shadow: 0 0 0 0 ${primaryColorTransparent};
  animation: pulse 1.4s infinite;

  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 ${primaryColorTransparent}; }
    70% { box-shadow: 0 0 0 8px transparent; }
    100% { box-shadow: 0 0 0 0 transparent; }
  }
`;

export const FilterSelect = styled.select`
  flex: 1;
  min-width: 0;
  min-height: 36px;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  padding: 6px 10px;
  background: ${cardBackground};
  color: var(--foreground);
  font-size: 13px;
`;

export const CatalogListToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const CatalogShowImagesToggle = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 4px 10px;
  border: 1px solid ${({$active}) => ($active ? primaryColor : borderColor)};
  border-radius: ${borderRadius};
  background: ${({$active}) => ($active ? primaryColorTransparent : cardBackground)};
  color: ${({$active}) => ($active ? primaryColor : mutedForeground)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${primaryColor};
    color: ${primaryColor};
  }
`;

export const CatalogListRow = styled.div<{ $selected?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--table-row-border);
  background: ${({$selected}) => ($selected ? 'var(--table-row-hover)' : 'transparent')};
  cursor: pointer;
  box-sizing: border-box;

  &:last-child {
    border-bottom: none;
  }
`;

export const CatalogRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

export const CatalogThumb = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--muted);
  border: 1px solid ${borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: ${mutedForeground};
  font-size: 11px;
  font-weight: 600;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* —— Touch / tiles catalog —— */

export const TouchPanel = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100dvh;
  background: ${cardBackground};
  box-shadow: none;
  display: flex;
  flex-direction: column;
`;

export const TouchSearchShell = styled(SearchShell)`
  min-height: 52px;
  padding: 10px 14px;

  ${SearchInput} {
    font-size: 16px; /* prevents iOS zoom */
  }
`;

export const CategoryChips = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 2px 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  flex-shrink: 0;
`;

export const CategoryChip = styled.button<{ $active?: boolean }>`
  flex: 0 0 auto;
  min-height: 48px;
  padding: 0 16px;
  border: 1px solid ${({$active}) => ($active ? primaryColor : borderColor)};
  border-radius: 999px;
  background: ${({$active}) => ($active ? primaryColorTransparent : cardBackground)};
  color: var(--foreground);
  font-size: 14px;
  font-weight: ${({$active}) => ($active ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;
  touch-action: manipulation;
  user-select: none;

  &:active {
    transform: scale(0.98);
  }
`;

export const TouchWorkspace = styled.div<{ $singlePick?: boolean }>`
  display: grid;
  grid-template-columns: ${({$singlePick}) => ($singlePick ? '1fr' : '1fr minmax(280px, 340px)')};
  gap: 12px;
  min-height: 0;
  flex: 1;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    grid-template-rows: ${({$singlePick}) => ($singlePick ? '1fr' : '1fr minmax(220px, 38%)')};
  }
`;

export const TouchTileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 12px;
  overflow: auto;
  align-content: start;
  padding: 2px;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
`;

export const VirtualScrollRoot = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  -webkit-overflow-scrolling: touch;
`;

export const VirtualTilesScrollRoot = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  align-content: start;
  padding: 2px;
  -webkit-overflow-scrolling: touch;
`;

export const SkeletonStack = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  overflow: hidden;
`;

export const SkeletonRow = styled.div`
  height: 52px;
  border-radius: ${borderRadius};
  background: linear-gradient(
    90deg,
    var(--muted) 0%,
    ${primaryColorTransparent} 50%,
    var(--muted) 100%
  );
  background-size: 200% 100%;
  animation: skeletonShimmer 1.2s ease-in-out infinite;

  @keyframes skeletonShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }
`;

export const TouchTile = styled.button<{ $selected?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 2px solid ${({$selected}) => ($selected ? primaryColor : borderColor)};
  border-radius: ${borderRadius};
  background: ${cardBackground};
  box-shadow: ${({$selected}) => ($selected ? `0 0 0 1px ${primaryColor}` : 'none')};
  cursor: pointer;
  text-align: left;
  color: var(--foreground);
  min-height: 188px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.985);
    background: var(--table-row-hover);
  }
`;

export const TouchTileImage = styled.div`
  position: relative;
  width: 100%;
  min-height: 0;
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  background: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: ${mutedForeground};
  font-size: 12px;
  flex: 1 1 auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const TouchTilePriceOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: 6px 8px;
  background: rgba(15, 23, 42, 0.62);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  text-align: left;
  overflow-wrap: anywhere;
  word-break: break-word;
  backdrop-filter: blur(2px);
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

export const TouchTileStock = styled.span`
  font-size: 11px;
  font-weight: 600;
  opacity: 0.92;
  white-space: nowrap;
`;

export const TouchTileInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  flex: 0 0 auto;
`;

export const TouchTileStockMeta = styled.span`
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.3;
  color: ${mutedForeground};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StockQty = styled.span`
  color: ${primaryColor};
  font-weight: 600;
  white-space: nowrap;
`;

export const TouchTileName = styled.div`
  width: 100%;
  max-width: 100%;
  font-size: 13px;
  font-weight: 700;
  color: var(--foreground);
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

export const TouchTileQtyBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 999px;
  background: ${primaryColor};
  color: ${primaryForeground};
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

export const CartPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  background: var(--muted);
  overflow: hidden;
`;

export const CartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid ${borderColor};
  background: ${cardBackground};
  flex-shrink: 0;
`;

export const CartList = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

export const CartItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid ${borderColor};
  background: ${cardBackground};
`;

export const CartItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

export const QtyStepper = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 48px;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const QtyStepButton = styled.button`
  width: 48px;
  height: 48px;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  background: ${cardBackground};
  color: var(--foreground);
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:active:not(:disabled) {
    background: ${primaryColorTransparent};
    border-color: ${primaryColor};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const QtyValue = styled.div`
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  background: var(--muted);
  font-size: 18px;
  font-weight: 700;
  color: var(--foreground);
`;

export const CartRemoveButton = styled.button`
  border: none;
  background: transparent;
  color: ${mutedForeground};
  font-size: 12px;
  padding: 4px 0;
  cursor: pointer;
  touch-action: manipulation;
  align-self: flex-start;

  &:active {
    color: var(--destructive, #ef4444);
  }
`;

export const TouchFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid ${borderColor};
  flex-shrink: 0;
  background: ${cardBackground};
`;

export const TouchPrimaryButton = styled.button<{ $secondary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 20px;
  border: 1px solid ${({$secondary}) => ($secondary ? borderColor : primaryColor)};
  border-radius: ${borderRadius};
  background: ${({$secondary}) => ($secondary ? cardBackground : primaryColor)};
  color: ${({$secondary}) => ($secondary ? 'var(--foreground)' : primaryForeground)};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

export const DetailsLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(2px);
  animation: detailsFadeIn 0.15s ease;

  @keyframes detailsFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

export const DetailsLoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: min(280px, 100%);
  max-width: 360px;
  padding: 20px 24px;
  border-radius: ${borderRadius};
  background: ${cardBackground};
  box-shadow: var(--card-shadow-lg);
  text-align: center;
  animation: detailsPopIn 0.18s ease;

  @keyframes detailsPopIn {
    from {
      opacity: 0;
      transform: translateY(6px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

export const DetailsLoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2.5px solid ${primaryColorTransparent};
  border-top-color: ${primaryColor};
  animation: detailsSpin 0.7s linear infinite;

  @keyframes detailsSpin {
    to { transform: rotate(360deg); }
  }
`;

export const ResultRowLoading = styled.span`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid ${primaryColorTransparent};
  border-top-color: ${primaryColor};
  animation: detailsSpin 0.7s linear infinite;
`;
